import React, { FC, memo, useCallback } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useGetCurrencySymbol } from 'app/appState';
import { TableFilter, useTableFilterContext } from 'features/TableFilter';
import { WarehouseSelect } from './WarehouseSelect';
import { CreditNotesListFilterKeys } from '../../model/types';
import { InputNumber } from 'shared/ui/InputNumber';
import { RangeDatePicker } from 'shared/ui/RangeDatePicker/RangeDatePicker';
import { CLIENT_DATE_FORMAT } from 'shared/utils/constants';
import { CreditNoteTypeSelect } from './CreditNoteTypeSelect';
import { ApplicabilitySelect } from './ApplicabilitySelect';
import { AccountTypeSelect } from './AccountTypeSelect';

export const CreditNotesListFilter: FC = memo(() => {
  const { t } = useAppTranslation(['contracts', 'common']);

  const currencySymbol = useGetCurrencySymbol();

  const { filters, changeFilters, clearCurrentFilters } = useTableFilterContext<CreditNotesListFilterKeys>();

  const selectWarehouse = useCallback(
    (value: string[]) => {
      changeFilters({
        key: CreditNotesListFilterKeys.selectedWarehouses,
        label: `${t('Selected')}: ${value.length}`,
        value,
        title: t('Warehouse'),
      });
    },
    [changeFilters, t],
  );

  const changeCreationDateFrom = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateFrom = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: CreditNotesListFilterKeys.creationDateFrom,
        label: t('From {{creationDateFrom}}', { creationDateFrom: clientFormatDateFrom }),
        value,
        title: t('Creation date'),
      });
    },
    [changeFilters, t],
  );

  const changeCreationDateTo = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateTo = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: CreditNotesListFilterKeys.creationDateTo,
        label: t('To {{creationDateTo}}', { creationDateTo: clientFormatDateTo }),
        value,
        title: t('Creation date'),
      });
    },
    [changeFilters, t],
  );

  const changeCreditNoteType = useCallback(
    (value: string[]) => {
      changeFilters({
        key: CreditNotesListFilterKeys.creditNoteType,
        label: `${t('Selected')}: ${value.length}`,
        value,
        title: t('Credit Note target'),
      });
    },
    [changeFilters, t],
  );

  const changeCreditedAmount = useCallback(
    (value: Nullable<number>) => {
      changeFilters({
        key: CreditNotesListFilterKeys.creditedAmount,
        label: `${value} ${currencySymbol}`,
        value,
        title: t('Credited amount'),
      });
    },
    [currencySymbol, changeFilters, t],
  );

  const changeContractApplicability = useCallback(
    (value: string, label: string) => {
      changeFilters({ key: CreditNotesListFilterKeys.isApplicable, label, value, title: t('Applicability') });
    },
    [changeFilters, t],
  );

  const changeAccountType = useCallback(
    (value: string, label: string) => {
      changeFilters({ key: CreditNotesListFilterKeys.accountType, label, value, title: t('Account type') });
    },
    [changeFilters, t],
  );

  return (
    <TableFilter>
      <div className="space-y-2">
        <WarehouseSelect values={filters.selectedWarehouses?.value} onChange={selectWarehouse} onClear={clearCurrentFilters} />

        <RangeDatePicker
          placeholder={[t('From'), t('To')]}
          label={t('Creation range')}
          onChangeFrom={changeCreationDateFrom}
          onChangeTo={changeCreationDateTo}
          values={[
            filters.creationDateFrom?.value && dayjs(filters.creationDateFrom?.value),
            filters.creationDateTo?.value && dayjs(filters.creationDateTo?.value),
          ]}
        />
        <div>
          <div className="mb-2">{t('Credited amount')}</div>
          <InputNumber placeholder={t('Credited amount')} value={filters.creditedAmount?.value} onChange={changeCreditedAmount} bordered />
        </div>

        <CreditNoteTypeSelect values={filters.creditNoteType?.value} onChange={changeCreditNoteType} onClear={clearCurrentFilters} />

        <div className="flex space-x-3 items-center">
          <ApplicabilitySelect value={filters.isApplicable?.value} onChange={changeContractApplicability} onClear={clearCurrentFilters} />
          <AccountTypeSelect value={filters.accountType?.value} onChange={changeAccountType} onClear={clearCurrentFilters} />
        </div>
      </div>
    </TableFilter>
  );
});
